/**
 * API client fino sobre fetch.
 *
 * Responsabilidades:
 *  - Injeta Authorization: Bearer <token> a partir do tokenStore
 *  - Normaliza erros em ApiError
 *  - 401 dispara handler global (usado para redirect para /login)
 *  - 403 dispara handler global (usado para mensagem amigavel)
 *  - Zero state — nao conhece React; composivel em hooks e testes
 *
 * JWT em localStorage e decisao provisional F1 (ver ADR-009 Secao 8).
 * Sera endurecido em F4.
 */

import { config } from './config';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;

  public constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface TokenStore {
  get(): string | null;
  set(token: string): void;
  clear(): void;
}

export const localStorageTokenStore: TokenStore = {
  get(): string | null {
    try {
      return window.localStorage.getItem(config.tokenStorageKey);
    } catch {
      return null;
    }
  },
  set(token: string): void {
    try {
      window.localStorage.setItem(config.tokenStorageKey, token);
    } catch {
      // silencia — modo privado pode bloquear
    }
  },
  clear(): void {
    try {
      window.localStorage.removeItem(config.tokenStorageKey);
      window.localStorage.removeItem(config.refreshTokenStorageKey);
    } catch {
      // silencia
    }
  },
};

type UnauthorizedHandler = () => void;
type ForbiddenHandler = () => void;
/**
 * Chamado quando o backend retorna 502 com codigo
 * `drive_token_expired_reconnect_required`. O handler deve exibir mensagem
 * amigavel ("Sua conexao com o Google Drive expirou. Reconecte em Sources.")
 * e oferecer CTA "Reconectar". NAO redireciona para login — o JWT do usuario
 * continua valido; apenas o token do Drive expirou.
 */
type DriveReconnectHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {};
let onForbidden: ForbiddenHandler = () => {};
let onDriveReconnectRequired: DriveReconnectHandler = () => {};

export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

export function setForbiddenHandler(fn: ForbiddenHandler): void {
  onForbidden = fn;
}

export function setDriveReconnectHandler(fn: DriveReconnectHandler): void {
  onDriveReconnectRequired = fn;
}

export interface ApiClientOptions {
  readonly tokenStore?: TokenStore;
  readonly fetchImpl?: typeof fetch;
  readonly baseUrl?: string;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly headers?: Record<string, string>;
  readonly auth?: boolean;
}

export class ApiClient {
  private readonly tokenStore: TokenStore;
  private readonly fetchImpl: typeof fetch;
  private readonly baseUrl: string;

  public constructor(opts: ApiClientOptions = {}) {
    this.tokenStore = opts.tokenStore ?? localStorageTokenStore;
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    this.baseUrl = opts.baseUrl ?? config.apiBaseUrl;
  }

  public async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(options.headers ?? {}),
    };
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (options.auth !== false) {
      const token = this.tokenStore.get();
      if (token !== null && token.length > 0) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const init: RequestInit = {
      method,
      headers,
    };
    if (options.body !== undefined) {
      init.body = JSON.stringify(options.body);
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    } catch (err) {
      throw new ApiError(err instanceof Error ? err.message : 'network_error', 0, 'network_error');
    }

    if (res.status === 401) {
      this.tokenStore.clear();
      onUnauthorized();
      throw new ApiError('unauthorized', 401, 'unauthorized');
    }
    if (res.status === 403) {
      onForbidden();
      throw new ApiError('forbidden', 403, 'forbidden');
    }
    // 429 — rate limit: erro conhecido, nao tenta fazer parse do body como payload de sucesso.
    // Retorna ApiError diretamente sem tentar JSON.parse (evita crash em respostas sem body).
    if (res.status === 429) {
      throw new ApiError('rate_limit_exceeded', 429, 'rate_limit_exceeded');
    }

    const text = await res.text();
    let payload: unknown = undefined;
    if (text.length > 0) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }

    if (!res.ok) {
      const code =
        payload && typeof payload === 'object' && 'error' in payload
          ? String((payload as { error: unknown }).error)
          : `http_${res.status}`;
      // 502 com codigo especifico de Drive expirado: nao e falha de auth interna.
      // Dispara handler dedicado para exibir mensagem amigavel + CTA Reconectar.
      // NAO limpa o JWT do usuario — apenas o token do Drive expirou.
      if (res.status === 502 && code === 'drive_token_expired_reconnect_required') {
        onDriveReconnectRequired();
      }
      throw new ApiError(code, res.status, code);
    }

    return payload as T;
  }

  public get<T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...opts, method: 'GET' });
  }

  public post<T>(
    path: string,
    body?: unknown,
    opts: Omit<RequestOptions, 'method' | 'body'> = {},
  ): Promise<T> {
    return this.request<T>(path, { ...opts, method: 'POST', body });
  }

  public put<T>(
    path: string,
    body?: unknown,
    opts: Omit<RequestOptions, 'method' | 'body'> = {},
  ): Promise<T> {
    return this.request<T>(path, { ...opts, method: 'PUT', body });
  }

  /**
   * Lista pastas do Google Drive de uma fonte conectada.
   * Se `parentId` for omitido, retorna a raiz do Drive.
   * Suporta paginacao via `pageToken`.
   * `view` controla qual secao do Drive listar: 'mine' (default) ou 'shared'
   * (pastas em "Compartilhados comigo").
   */
  public getDriveFolders(
    sourceId: string,
    parentId?: string,
    pageToken?: string,
    view?: 'mine' | 'shared',
  ): Promise<DriveFoldersResponse> {
    const params = new URLSearchParams();
    if (parentId !== undefined && parentId.length > 0) {
      params.set('parentId', parentId);
    }
    if (pageToken !== undefined && pageToken.length > 0) {
      params.set('pageToken', pageToken);
    }
    if (view !== undefined) {
      params.set('view', view);
    }
    const qs = params.toString();
    const path = `/sources/google_drive/${sourceId}/folders${qs.length > 0 ? `?${qs}` : ''}`;
    return this.get<DriveFoldersResponse>(path);
  }

  /**
   * Persiste o `folderId` selecionado para a fonte do Drive.
   * Retorna o folder confirmado pelo backend.
   */
  public setDriveFolder(sourceId: string, folderId: string): Promise<DriveFolderSetResponse> {
    return this.put<DriveFolderSetResponse>(`/sources/google_drive/${sourceId}/folder`, {
      folderId,
    });
  }
}

// ---- tipos do contrato Drive Picker ----

export interface DriveFolder {
  readonly id: string;
  readonly name: string;
  readonly modifiedTime: string;
}

export interface DriveFoldersResponse {
  readonly folders: readonly DriveFolder[];
  readonly nextPageToken?: string;
}

export interface DriveFolderSetResponse {
  readonly ok: true;
  readonly folder: {
    readonly id: string;
    readonly name: string;
  };
}

export const apiClient = new ApiClient();
